import React, { useState } from 'react';
import { MapPin, Phone, Mail, MessageSquare, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { trackPhoneClick, trackWhatsAppClick, trackFormSubmit, trackEvent } from '../utils/analytics';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Track form submission in Google Analytics
      trackFormSubmit('contact_form', {
        form_subject: formData.subject,
        has_phone: !!formData.phone
      });

      // Create WhatsApp message with form data
      const whatsappMessage = `New Contact Form Submission:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Subject: ${formData.subject}
Message: ${formData.message}`;

      const whatsappURL = `https://wa.me/916358585323?text=${encodeURIComponent(whatsappMessage)}`;
      
      // Create email body
      const emailSubject = `Contact Form: ${formData.subject}`;
      const emailBody = `Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Subject: ${formData.subject}

Message:
${formData.message}`;

      const emailURL = `mailto:carepharmacyindia@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

      // Track WhatsApp and email clicks
      trackWhatsAppClick('916358585323', 'contact_form');
      trackEvent('email_click', {
        email: 'carepharmacyindia@gmail.com',
        location: 'contact_form'
      });

      // Open both WhatsApp and email
      window.open(whatsappURL, '_blank');
      window.open(emailURL, '_blank');

      toast({
        title: 'Redirected!',
        description: 'WhatsApp and email links opened. Please send your message through your preferred platform.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'There was an issue processing your request. Please try again.'
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              We're Here to Help
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              Get in touch for franchise enquiries, customer care, partnerships and media inquiries.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            <div className="hover-lift" style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <Phone size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Franchise Enquiries
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Interested in opening a Care Pharmacy franchise?
              </p>
              <a 
                href="tel:+918200886945" 
                className="body-regular" 
                style={{
                  color: 'var(--brand-blue)',
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
                onClick={() => trackPhoneClick('918200886945', 'franchise_enquiries')}
              >
                +91-8200886945
              </a>
            </div>

            <div className="hover-lift" style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <Phone size={40} style={{ color: 'var(--brand-green)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Customer Care
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                For orders, queries and support
              </p>
              <a 
                href="tel:+916358585323" 
                className="body-regular" 
                style={{
                  color: 'var(--brand-blue)',
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
                onClick={() => trackPhoneClick('916358585323', 'customer_care')}
              >
                +91-6358585323
              </a>
            </div>

            <div className="hover-lift" style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <MessageSquare size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                WhatsApp Orders
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Order medicines via WhatsApp
              </p>
              <a 
                href="https://wa.me/918160510969" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="body-regular" 
                style={{
                  color: 'var(--brand-blue)',
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
                onClick={() => trackWhatsAppClick('918160510969', 'whatsapp_orders')}
              >
                +91-8160510969
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '60px',
            alignItems: 'start'
          }} className="contact-form-grid">
            {/* Contact Form */}
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Send Us a Message
              </h2>
              <p className="body-regular" style={{
                color: 'var(--text-secondary)',
                marginBottom: '32px'
              }}>
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      height: '48px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px'
                    }}
                  />
                </div>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      height: '48px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px'
                    }}
                  />
                </div>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Phone
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      height: '48px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px'
                    }}
                  />
                </div>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Subject *
                  </label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    style={{
                      height: '48px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px'
                    }}
                  />
                </div>
                <div>
                  <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    style={{
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      minHeight: '140px'
                    }}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={16} />
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '32px' }}>
                Contact Information
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                    Corporate Office
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <MapPin size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      Care Pharmacy Pvt Ltd<br />
                      Wall Street-2, Opp. Orient Club<br />
                      Gujarat College Road<br />
                      Ahmedabad-380006, Gujarat, India
                    </p>
                  </div>
                </div>

                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                    Email Us
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <Mail size={18} style={{ color: 'var(--text-secondary)' }} />
                      <a href="mailto:info@carepharmacy.co" className="body-regular" style={{
                        color: 'var(--text-secondary)',
                        textDecoration: 'none'
                      }}>
                        info@carepharmacy.co
                      </a>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <Mail size={18} style={{ color: 'var(--text-secondary)' }} />
                      <a href="mailto:carepharmacyindia@gmail.com" className="body-regular" style={{
                        color: 'var(--text-secondary)',
                        textDecoration: 'none'
                      }}>
                        carepharmacyindia@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                    Business Hours
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Monday - Saturday: 9:00 AM - 7:00 PM
                  </p>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Sunday: Closed
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-light)', marginTop: '12px' }}>
                    Store hours may vary by location
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 640px) {
          .contact-form-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
        @media (max-width: 360px) {
          .contact-form-grid {
            gap: 24px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;